import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { addActionNameToMutationResult } from 'shared/hooks/utils/add-action-name-to-mutation-result';
import { useAxiosInstance } from 'shared/hooks/utils/axios-instance';
import type { ApiError, ApiResponse } from 'shared/hooks/utils/types';
import z from 'zod/v4';
import { CreateTaskVariablesSchema } from './use-create-task';

const updateTaskVariablesSchema = z.object({
  id: z.string(),
  data: CreateTaskVariablesSchema.partial(),
});

export type UpdateTaskVariables = z.infer<typeof updateTaskVariablesSchema>;

export async function updateTaskFn(
  axiosInstance: Promise<AxiosInstance>,
  data: UpdateTaskVariables,
) {
  const instance = await axiosInstance;
  return instance.put(`/tasks/${data.id}`, data.data);
}

export function useUpdateTask() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, UpdateTaskVariables>({
    mutationFn: variables => updateTaskFn(axiosInstance, variables),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['tasks'] });
      client.invalidateQueries({ queryKey: ['task'] });
    },
  });

  return addActionNameToMutationResult<ApiResponse, ApiError, UpdateTaskVariables, 'updateTask'>(
    'updateTask',
    mutation,
  );
}
